import React, { FC, memo, useEffect, useState } from 'react';
import { Skeleton, Slider, useMediaQuery, useTheme } from '@mui/material';
import { isEqual } from 'lodash';
import { useLocation } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedFunction } from '../../helpers/customHooks';
import { useUtils } from '../../services/useUtils';
import { FilterParams } from '../../store/enums';

const useStyles = makeStyles()((theme) => ({
  container: {
    position: 'relative',
    width: '100%',
    padding: '20px 0 8px 0',
    [theme.breakpoints.down(600)]: {
      padding: '2px 0 0 0',
    },
  },
  currentValues: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#FFFFFF',
    [theme.breakpoints.down(600)]: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      marginBottom: '4px',
      gap: '4px',
      lineHeight: 1.2,
    },
  },
  priceLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px',
    fontSize: '0.875rem',
    color: 'rgba(255, 255, 255, 0.65)',
    [theme.breakpoints.down(600)]: {
      fontSize: '0.625rem',
      marginTop: '3px',
      lineHeight: 1.2,
    },
  },
  staticPrice: {
    fontSize: '0.875rem',
    color: '#FFFFFF',
    [theme.breakpoints.down(600)]: {
      fontSize: '0.625rem',
      color: 'rgba(255, 255, 255, 0.65)',
    },
  },
  rail: {
    backgroundColor: '#6B6B6B',
    [theme.breakpoints.down(600)]: {
      height: 3,
      opacity: 1,
    },
  },
  track: {
    background:
      'radial-gradient(127.6% 7975000.38% at -14.6% -5099.56%, #FFF388 0%, #FFF388 20.83%, #FFDE52 35.94%, #FFBF00 50%, #FF7700 63.54%, #FF3D00 98.44%)',
    border: '0px solid currentColor',
    [theme.breakpoints.down(600)]: {
      height: 3,
    },
  },
  thumb: {
    background: '#1C1C1C',
    width: '24px',
    height: '24px',
    border: '1px solid #FFFFFF',
    [theme.breakpoints.down(600)]: {
      width: '18px',
      height: '18px',
    },
  },
  slider: {
    padding: '6px 0',
    [theme.breakpoints.down(600)]: {
      padding: '2px 0 4px',
    },
  },
  mobileHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 4,
    flexWrap: 'wrap',
    rowGap: 2,
  },
  mobileCurrentRange: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: '#FFFFFF',
    lineHeight: 1.2,
  },
  mobileBounds: {
    fontSize: '0.625rem',
    color: 'rgba(255, 255, 255, 0.55)',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  },
  skeleton: {
    transform: 'scale(1)',
  },
}));

export const RangePriceSlider: FC<RangePriceSliderType> = memo(
  ({ currency, priceRange }) => {
    const { classes } = useStyles();
    const theme = useTheme();
    const isCompactPriceLayout = useMediaQuery(theme.breakpoints.down(600));
    const location = useLocation();

    const [values, setValues] = useState<number[]>(priceRange.map(val => Math.round(val)));
    const [searchParams, setSearchParams] = useSearchParams();

    const { getEmojiFlagByCurrency } = useUtils();

    const symbolCurrency = getEmojiFlagByCurrency(currency);

    const tooltipText = (value: number) => `${symbolCurrency} ${value}`;

    const setPriceParams = (from: number, to: number) => {
      setSearchParams((params) => {
        params.set(FilterParams.Price, `${from}-${to}`);
        return params;
      });
    };
    const debouncedPriceParams = useDebouncedFunction(setPriceParams, 300);

    const handleChange = async (event: Event, newValue: number[], activeThumb: number) => {
      if (!Array.isArray(newValue)) {
        return;
      }

      const [min, max] = newValue.map(val => Math.round(val));
      const minDistance = 50;

      if (max - min < minDistance) {
        if (activeThumb === 0) {
          const clamped = Math.min(min, max - minDistance);
          setValues([clamped, clamped + minDistance]);
        } else {
          const clamped = Math.max(max, minDistance);
          setValues([clamped - minDistance, clamped]);
        }
      } else {
        setValues([min, max]);
      }

      debouncedPriceParams(min, max);
    };

    useEffect(() => {
      if (searchParams.has(FilterParams.Price)) {
        const paramRange = searchParams.get(FilterParams.Price).split('-');
        setValues([Math.round(+paramRange.at(0)), Math.round(+paramRange.at(1))]);
      } else {
        setValues(priceRange.map(val => Math.round(val)));
      }
    }, [location.search, priceRange]);

    if (!priceRange.at(0)) {
      return <Skeleton className={classes.skeleton} animation={'wave'} width={'100%'} height={'100%'} />;
    }

    const [minValue, maxValue] = values.map(val => Math.round(val));
    const [minPrice, maxPrice] = priceRange.map(val => Math.round(val));

    const sliderEl = (
      <Slider
        className={classes.slider}
        classes={{
          rail: classes.rail,
          track: classes.track,
          thumb: classes.thumb,
        }}
        value={values}
        min={minPrice}
        max={maxPrice}
        onChange={handleChange}
        valueLabelDisplay={'off'}
        getAriaValueText={tooltipText}
        disableSwap
      />
    );

    return (
      <div className={classes.container}>
        {isCompactPriceLayout ? (
          <>
            <div className={classes.mobileHeaderRow}>
              <span className={classes.mobileCurrentRange}>
                From {symbolCurrency} {minValue} to {symbolCurrency} {maxValue}
              </span>
              <span className={classes.mobileBounds}>
                {symbolCurrency} {minPrice} – {symbolCurrency} {maxPrice}
              </span>
            </div>
            {sliderEl}
          </>
        ) : (
          <>
            <div className={classes.currentValues}>
              <span>From {symbolCurrency} {minValue}</span>
              <span>to {symbolCurrency} {maxValue}</span>
            </div>
            {sliderEl}
            <div className={classes.priceLabels}>
              <span className={classes.staticPrice}>
                {symbolCurrency} {minPrice}
              </span>
              <span className={classes.staticPrice}>
                {symbolCurrency} {maxPrice}
              </span>
            </div>
          </>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
);

type RangePriceSliderType = {
  priceRange: number[];
  currency: string;
};
